// Wallet vouchers / compensations — one write path shared by Marketing › Vouchers
// and the Rated Orders case modal (RATED_ORDERS_PLAN.md D6). The document shape and
// the "Compensation Granted" push are byte-for-byte what the Vouchers modal wrote
// before the extraction; wallet analytics (functions/helpers.js CalculatePoints /
// CalculateRedeemedTotals) classify by `type === 'voucher'` + `description`.
import { firestore, functions } from './Firebase';

export const VOUCHER_DESCRIPTIONS = ['Voucher', 'Compensation'];

export const voucherPushCopy = (description, amount) => (description === 'Voucher'
  ? { title: 'Congratulations! You Have a New Voucher', body: `Great news! You've received a voucher worth ${amount} AED. Check your account to redeem it now.` }
  : { title: 'Compensation Granted', body: `We apologize for any inconvenience. You've been compensated with a voucher worth ${amount} AED. Please check your account to redeem it.` });

/** Resolve a customer uid from a phone number via the getUserByPhoneNumber callable. Returns null when not found. */
export async function findUserByPhone(phone) {
  const number = `+${String(phone || '').replace(/\D/g, '')}`;
  const result = await functions().httpsCallable('getUserByPhoneNumber')({ phoneNumber: number });
  if (result?.data?.error || !result?.data?.user) return null;
  return result.data.user;
}

/** Push "voucher / compensation granted" to the customer (no-op without an fcm token). */
export async function notifyVoucherGranted({ userID, description, amount }) {
  const userSnap = await firestore().collection('users').doc(userID).get();
  const token = userSnap?.data()?.fcm_token;
  if (!token) return false;
  await firestore().collection('notifications').add({ user: userID, to: token, ...voucherPushCopy(description, amount), read: false });
  return true;
}

/**
 * Write one voucher (or N split vouchers) to users/{userID}/walletLog and push once.
 * `voucher` is the modal's form object: { customer, type, description, amount, exp, minAmount?, numberOfSplits?, key?, addNew? }.
 * `extra` fields are merged into every wallet doc (e.g. { bookingID, complaint: true } from the queue).
 * Returns the wallet log ids written.
 */
export async function issueWalletVoucher({ userID, voucher, extra = {}, notify = true }) {
  if (!userID) throw new Error('Customer not found');
  const v = JSON.parse(JSON.stringify(voucher || {}));
  if (!v.amount || !v.exp || !v.description) throw new Error('Amount, expiry and type are required');
  const col = firestore().collection('users').doc(userID).collection('walletLog');
  const ids = [];
  const splits = Number(v.numberOfSplits) || 0;
  if (splits > 1) {
    for (let i = 0; i < splits; i++) {
      const ref = col.doc();
      const split = JSON.parse(JSON.stringify(v));
      split.amount = Number(v.amount / splits).toFixed(2);
      split.key = ref.id; split.updated = Date.now(); split.userID = userID; split.created = Date.now(); split.type = 'voucher';
      delete split.numberOfSplits; delete split.addNew;
      await ref.set({ ...split, ...extra }, { merge: true });
      ids.push(ref.id);
    }
  } else {
    const id = v.key || v.id || col.doc().id;
    v.key = id;
    if (v.addNew) v.created = Date.now();
    delete v.addNew; delete v.numberOfSplits;
    v.updated = Date.now(); v.userID = userID; v.type = 'voucher';
    await col.doc(id).set({ ...v, ...extra }, { merge: true });
    ids.push(id);
  }
  if (notify) {
    try { await notifyVoucherGranted({ userID, description: v.description, amount: v.amount }); }
    catch (error) { console.log('error notifications voucher granted', error); }
  }
  return ids;
}
